import { JSX, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useParams } from "react-router-dom";

import MovieCardSkeleton from "@/components/cards/MovieCard/MovieCard.skeleton";
import GroupTransition from "@/components/helpers/GroupTransition/GroupTransition";
import MoviesGrid from "@/components/helpers/MoviesGrid";
import { IconSadFace } from "@/components/icons";

import { useGetContent, useGetDistributors } from "@/hooks/api/content.hooks";

const DistributorPage = (): JSX.Element => {
  const { id } = useParams() as { id: string };
  const { t } = useTranslation();
  const { ref: inViewRef, inView } = useInView();
  const { data: distributors } = useGetDistributors();
  const CurrentDistributor = distributors.find((dist) => dist.id === +id);

  const { data, isFetching, fetchNextPage, hasNextPage } = useGetContent({ distributor: id, limit: 48 });

  const flattenedData = data?.pages.flatMap((page) => page.results) || [];

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetching]);

  return (
    <>
      <h1 className="text-[36px] font-semibold max-lg:px-4 max-lg:text-[26px]">{CurrentDistributor?.name}</h1>
      <GroupTransition<HTMLDivElement> transitionKey={id}>
        {({ ref }) => (
          <div ref={ref}>
            <section className={"rounded-7.5 max-lg:py-0"}>
              {flattenedData && flattenedData.length ? (
                <MoviesGrid content={flattenedData} />
              ) : !isFetching ? (
                <div className={"flex flex-col items-center"}>
                  <IconSadFace className={"size-20 fill-grey-1"} />
                  <div className={"mt-4 max-w-[27.5rem] text-center"}>
                    <h1 className={"text-2xl font-semibold"}>{t("error.nothing-found")}</h1>
                    <p className={"mt-2 text-lg/6 text-on-surface"}>{t("error.nothing-found-message")}</p>
                  </div>
                </div>
              ) : null}
              {isFetching ? (
                <div className={"movies-grid mt-[30px] max-lg:mt-3"}>
                  {[...Array(18).keys()].map((_, index) => (
                    <MovieCardSkeleton key={index} />
                  ))}
                </div>
              ) : null}
              {data?.pages.length ? <div ref={inViewRef} className={"h-px w-full"} /> : null}
            </section>
          </div>
        )}
      </GroupTransition>
    </>
  );
};

export default DistributorPage;
